import { GeneralSelect, OutsideLink } from '@clickcease/cc-client-components-lib'
import React, { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useGetFacebookAdSets } from '../../../../../api/getFacebookAdSets'
import { useSetSelectedFacebookAdSets } from '../../../../../api/setSelectedFacebookAdSets'
import { PaginatedCheckboxModal } from '../../../../common/PaginatedCheckboxModal/PaginatedCheckboxModal'
import { useCheckboxPagination } from '../../../../../../../hooks/pagination/useCheckboxPagination'
import {
  DEFAULT_PAGINATION_PARAMS,
  PaginationMetadata,
  PaginationParams,
} from '../../../../../../../types/pagination'
import { FB_LINKS } from '../../../../../../../utils/facebookLinks'

import { useSelectAdsRequests } from '../SelectAdSets/useSelectAdsRequests'

interface PaginatedSelectAdSetsProps {
  accountId: number
  onSubmit: (ids: number[]) => void
  onBack: () => void
  isBackDisabled?: boolean
  setIsAutoProtected: (value: boolean) => void
}

export const PaginatedSelectAdSets = ({
  accountId,
  isBackDisabled,
  onSubmit,
  onBack,
  setIsAutoProtected,
}: PaginatedSelectAdSetsProps) => {
  const { t } = useTranslation('PMPlatforms')
  const [selectedAdAccount, setSelectedAdAccount] = useState<number>()
  const [errorText, setErrorText] = useState<string | undefined>()
  const [paginationParams, setPaginationParams] =
    useState<PaginationParams>(DEFAULT_PAGINATION_PARAMS)

  const { accountOptions, isAdAccountsAdSetsAutoProtected, selectedAccountOption } =
    useSelectAdsRequests({
      setSelectedAdAccount,
      selectedAdAccount,
      accountId,
      dontGetAdSets: true,
    })

  useEffect(() => {
    setIsAutoProtected(!!isAdAccountsAdSetsAutoProtected)
  }, [isAdAccountsAdSetsAutoProtected, setIsAutoProtected])

  const { data: adSetsData } = useGetFacebookAdSets({
    connectionId: accountId,
    adAccountId: selectedAdAccount,
    pagination: paginationParams,
    config: {
      enabled: !!selectedAdAccount,
    },
  })

  const getMetaDataParam = (param: keyof PaginationMetadata) => {
    if (adSetsData instanceof Error || !adSetsData) return undefined
    return adSetsData?.metadata?.[param] as number | boolean | undefined
  }

  const adSets = useMemo(
    () => (adSetsData instanceof Error || !adSetsData?.adSets ? [] : adSetsData.adSets),
    [adSetsData]
  )

  const {
    handleCheckboxClicked,
    handleSelectAll,
    selectedOptions,
    page,
    search,
    pageCount,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPrevPage,
    searchPage,
    cleanSelection,
    setPageCount,
    sort,
    setSort,
    selectedText,
    paginationParams: hookPaginationParams,
  } = useCheckboxPagination({
    entities: adSets,
    totalCount: getMetaDataParam('totalCount') as number,
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPageCount(getMetaDataParam('pageCount') as number | undefined), [adSetsData]) // update page count
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => cleanSelection(), [selectedAdAccount]) // clean selection on account change
  useEffect(() => setPaginationParams(hookPaginationParams), [hookPaginationParams]) // update pagination params

  const { mutate, isLoading } = useSetSelectedFacebookAdSets({
    connectionId: accountId,
  })

  const handleSubmit = () => {
    if (selectedOptions.length > 0 && selectedAdAccount) {
      mutate(
        { adSetIds: selectedOptions, adAccountId: selectedAdAccount },
        {
          onSuccess: (data) => {
            if (!(data instanceof Error)) {
              onSubmit(selectedOptions)
              setErrorText(undefined)
            } else
              setErrorText(
                data?.response?.data.message || t('meta-connection-wizard.addsets-fail-to-add')
              )
          },
        }
      )
    } else onSubmit(selectedOptions)
  }

  const isMessageBoxCustomAText = isAdAccountsAdSetsAutoProtected || errorText
  const messageBoxType = errorText ? 'failure' : isMessageBoxCustomAText ? 'info' : 'warning'

  return (
    <PaginatedCheckboxModal
      title={t('meta-connection-wizard.adsets-title')}
      searchPlaceholder={t('meta-connection-wizard.adsets-search')}
      entitiesName={t('meta-connection-wizard.ad-set')}
      buttonText={t('next-button')}
      buttonLoading={isLoading}
      filterBy={search}
      options={adSets}
      selected={selectedOptions}
      selectedText={selectedText}
      pageCount={pageCount ?? 1}
      totalItems={getMetaDataParam('totalCount') as number}
      isBackButtonDisabled={isBackDisabled}
      onSearch={searchPage}
      onBack={onBack}
      page={page}
      sort={sort}
      isReadOnly={isAdAccountsAdSetsAutoProtected}
      setSort={setSort}
      onFirstPage={onFirstPage}
      onLastPage={onLastPage}
      onNextPage={onNextPage}
      handleSelect={handleCheckboxClicked}
      handleSubmit={handleSubmit}
      handleSelectAll={handleSelectAll}
      onPreviousPage={onPrevPage}
      customInput={
        <GeneralSelect
          options={accountOptions}
          value={selectedAccountOption}
          placeholder={t('meta-connection-wizard.account-selection-placeholder')}
          wrapperCSS={{ width: '240px' }}
          onChange={(item) => {
            if (item) setSelectedAdAccount(parseInt(item.value))
          }}
        />
      }
      messageBox={{
        content: errorText ? (
          <span>{errorText}</span>
        ) : (
          <Trans
            t={t}
            i18nKey={
              isAdAccountsAdSetsAutoProtected
                ? 'meta-connection-wizard.adsets-auto-protected'
                : 'meta-connection-wizard.adsets-message'
            }
            components={{
              1: (
                <OutsideLink
                  href={FB_LINKS.learningPhase}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              b: <b />,
            }}
          />
        ),
        type: messageBoxType,
      }}
    />
  )
}
